/**
 * DOM Observer
 * Watches for dynamically added content using MutationObserver
 * Triggers translation of new content (AJAX, SPA navigation, etc.)
 */

export interface DOMObserverCallbacks {
  onNewContent?: (element: Element) => void;
  onNewSwitchers?: () => void;
}

export class DOMObserver {
  private observer: MutationObserver | null = null;
  private callbacks: DOMObserverCallbacks;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingElements: Set<Element> = new Set();
  private debounceMs: number;

  constructor(callbacks: DOMObserverCallbacks, debounceMs = 50) {
    this.callbacks = callbacks;
    this.debounceMs = debounceMs;
  }

  /**
   * Start observing DOM changes
   */
  start(): void {
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      this.handleMutations(mutations);
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Stop observing
   */
  stop(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingElements.clear();
  }

  /**
   * Handle DOM mutations
   */
  private handleMutations(mutations: MutationRecord[]): void {
    let hasNewSwitchers = false;

    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const element = node as Element;

        // Check for language switchers
        if (this.hasLanguageSwitcher(element)) {
          hasNewSwitchers = true;
        }

        // Queue for auto-translation
        if (this.callbacks.onNewContent) {
          this.pendingElements.add(element);
        }
      }
    }

    // Trigger switcher callback immediately
    if (hasNewSwitchers && this.callbacks.onNewSwitchers) {
      this.callbacks.onNewSwitchers();
    }

    // Debounce content translation
    if (this.pendingElements.size > 0) {
      this.scheduleFlush();
    }
  }

  /**
   * Schedule debounced processing of pending elements
   */
  private scheduleFlush(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.flushPendingElements();
    }, this.debounceMs);
  }

  /**
   * Process all pending elements
   */
  private flushPendingElements(): void {
    if (!this.callbacks.onNewContent || this.pendingElements.size === 0) {
      return;
    }

    for (const element of this.pendingElements) {
      // Skip if element was removed from DOM
      if (!document.body.contains(element)) {
        continue;
      }
      this.callbacks.onNewContent(element);
    }

    this.pendingElements.clear();
  }

  /**
   * Check if element or children are language switchers
   */
  private hasLanguageSwitcher(element: Element): boolean {
    return (
      element.hasAttribute('data-lionrapid-switcher') ||
      element.querySelector('[data-lionrapid-switcher]') !== null
    );
  }
}
